/**
 * Screenshot Mode Detection and Configuration
 * Utility for Fastlane screenshot automation with Capacitor
 */

import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { Logger } from './Logger';


export class ScreenshotMode {
  private static _isActive: boolean | null = null
  private static _config: ScreenshotConfig | null = null
  private static screenshotStage = 0
  private static stageStartTime = 0
  private static initPromise: Promise<void> | null = null

  /**
   * Initialize screenshot mode detection (async)
   */
  static async initialize(): Promise<void> {
    if (this.initPromise) return this.initPromise

    this.initPromise = (async () => {
      this._isActive = await this.detectScreenshotMode()
      this._config = this.parseConfig()

      if (this._isActive) {
        Logger.getInstance().log('🎬 Screenshot mode initialized:', this._config)
      }
    })()

    return this.initPromise
  }

  /**
   * Check if running in screenshot mode
   */
  static get isActive(): boolean {
    if (this._isActive === null) {
      console.warn('ScreenshotMode.initialize() should be called before accessing isActive')
      return false
    }
    return this._isActive
  }

  /**
   * Get screenshot configuration
   */
  static get config(): ScreenshotConfig {
    if (this._config === null) {
      this._config = this.parseConfig()
    }
    return this._config
  }

  /**
   * Get current screenshot stage (0-5)
   * Used to determine which automated sequence to show
   */
  static get currentStage(): number {
    return this.screenshotStage
  }

  /**
   * Advance to next screenshot stage
   */
  static nextStage(): void {
    this.screenshotStage++
    this.stageStartTime = Date.now()
    Logger.getInstance().log(`📸 Screenshot stage advanced to: ${this.screenshotStage}`)
  }

  /**
   * Get time elapsed in current stage (milliseconds)
   */
  static getStageElapsed(): number {
    return Date.now() - this.stageStartTime
  }

  /**
   * Detect screenshot mode from Capacitor launch arguments or URL params
   */
  private static async detectScreenshotMode(): Promise<boolean> {
    Logger.getInstance().log('🎬 [ScreenshotMode] Starting detection...')

    // Check URL parameters first (works in browser and can be set manually)
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      Logger.getInstance().log('🎬 [ScreenshotMode] URL params:', window.location.search)
      if (urlParams.has('SNAPSHOT') || urlParams.has('screenshot')) {
        Logger.getInstance().log('🎬 [ScreenshotMode] ✅ Detected via URL parameter')
        return true
      }
    }

    // Check Capacitor Preferences (set by iOS AppDelegate)
    if (Capacitor.isNativePlatform()) {
      Logger.getInstance().log('🎬 [ScreenshotMode] Running on native platform, checking Preferences...')
      try {
        const { value } = await Preferences.get({ key: 'SNAPSHOT_MODE' })
        Logger.getInstance().log('🎬 [ScreenshotMode] Preferences.SNAPSHOT_MODE =', value)
        if (value === 'true') {
          Logger.getInstance().log('🎬 [ScreenshotMode] ✅ Detected via Capacitor Preferences (iOS launch args)')
          return true
        }
      } catch (error) {
        Logger.getInstance().log('🎬 [ScreenshotMode] ⚠️  Could not check Capacitor preferences:', error)
      }
    } else {
      Logger.getInstance().log('🎬 [ScreenshotMode] Not on native platform')
    }

    // Check localStorage fallback
    if (typeof localStorage !== 'undefined') {
      const snapshotMode = localStorage.getItem('SNAPSHOT_MODE')
      Logger.getInstance().log('🎬 [ScreenshotMode] localStorage.SNAPSHOT_MODE =', snapshotMode)
      if (snapshotMode === 'true') {
        Logger.getInstance().log('🎬 [ScreenshotMode] ✅ Detected via localStorage')
        return true
      }
    }

    Logger.getInstance().log('🎬 [ScreenshotMode] ❌ Not detected - normal mode')
    return false
  }

  /**
   * Parse screenshot configuration from URL or defaults
   */
  private static parseConfig(): ScreenshotConfig {
    const config: ScreenshotConfig = {
      disableAnimations: true,
      deterministicGameplay: true,
      skipIntro: true,
      autoNavigate: true,
      debugUI: false,
      gameMode: 'endless',
      difficulty: 'Normal',
      scenario: 'default',
      screenshotDelay: 2000,
      // Scenario triggers
      triggerLevelUp: false,
      triggerBigMatch: false,
      triggerCombo: false,
      triggerAchievement: false
    }

    // Parse from URL parameters
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)

      // Parse scenario type
      const scenario = urlParams.get('scenario') || urlParams.get('SCENARIO')
      if (scenario) {
        config.scenario = scenario

        // Enable specific triggers based on scenario
        if (scenario === 'levelup') config.triggerLevelUp = true
        if (scenario === 'bigmatch') config.triggerBigMatch = true
        if (scenario === 'combo') config.triggerCombo = true
        if (scenario === 'achievement') config.triggerAchievement = true
      }

      if (urlParams.has('GAME_MODE')) {
        config.gameMode = urlParams.get('GAME_MODE') as 'endless' | 'timeattack'
      }

      if (urlParams.has('DEBUG_UI')) {
        config.debugUI = true
      }
    }

    // Also check localStorage for scenario (set by AppDelegate from launch args)
    if (typeof localStorage !== 'undefined') {
      const storedScenario = localStorage.getItem('SNAPSHOT_SCENARIO')
      if (storedScenario) {
        config.scenario = storedScenario

        // Enable specific triggers based on scenario
        if (storedScenario === 'levelup') config.triggerLevelUp = true
        if (storedScenario === 'bigmatch') config.triggerBigMatch = true
        if (storedScenario === 'combo') config.triggerCombo = true
        if (storedScenario === 'achievement') config.triggerAchievement = true
      }
    }

    return config
  }

  /**
   * Should auto-navigate to game from title screen
   */
  static shouldAutoNavigate(): boolean {
    return this.isActive && this.config.autoNavigate
  }

  /**
   * Get the game mode to auto-select
   */
  static getAutoGameMode(): 'ENDLESS' | 'TIME_ATTACK' {
    return this.config.gameMode === 'timeattack' ? 'TIME_ATTACK' : 'ENDLESS'
  }

  /**
   * Setup deterministic gameplay for consistent screenshots
   */
  static setupDeterministicGameplay(): void {
    if (!this.isActive || !this.config.deterministicGameplay) return

    Logger.getInstance().log('🎲 Setting up deterministic gameplay for screenshots')

    // Override Math.random with seeded random for consistent game states
    const seed = 12345
    let state = seed
    Math.random = () => {
      state = (state * 1103515245 + 12345) & 0x7fffffff
      return state / 0x7fffffff
    }
  }

  /**
   * Get game initialization data for screenshot mode
   */
  static getGameInitData(): any {
    if (!this.isActive) return {}

    return {
      screenshotMode: true,
      selectedMode: this.config.gameMode === 'timeattack' ? 'TIME_ATTACK' : 'ENDLESS',
      difficulty: this.config.difficulty,
      loadSavedGame: false,
      scenario: this.config.scenario
    }
  }

  /**
   * Should trigger a level up scenario
   */
  static shouldTriggerLevelUp(): boolean {
    return this.isActive && this.config.triggerLevelUp
  }

  /**
   * Should trigger a big match scenario
   */
  static shouldTriggerBigMatch(): boolean {
    return this.isActive && this.config.triggerBigMatch
  }

  /**
   * Should trigger a combo scenario
   */
  static shouldTriggerCombo(): boolean {
    return this.isActive && this.config.triggerCombo
  }

  /**
   * Should trigger an achievement scenario
   */
  static shouldTriggerAchievement(): boolean {
    return this.isActive && this.config.triggerAchievement
  }

  /**
   * Get moves to execute for the current scenario
   * Returns an array of {fromRow, fromCol, toRow, toCol} moves
   */
  static getScenarioMoves(): Array<{ fromRow: number, fromCol: number, toRow: number, toCol: number }> | null {
    if (!this.isActive) return null

    const scenarios: Record<string, Array<{ fromRow: number, fromCol: number, toRow: number, toCol: number }>> = {
      'bigmatch': [
        { fromRow: 2, fromCol: 2, toRow: 2, toCol: 3 },
        { fromRow: 3, fromCol: 2, toRow: 2, toCol: 2 },
      ],
      'combo': [
        { fromRow: 1, fromCol: 1, toRow: 1, toCol: 2 },
        { fromRow: 2, fromCol: 3, toRow: 3, toCol: 3 },
      ],
      'levelup': [
        { fromRow: 0, fromCol: 0, toRow: 0, toCol: 1 },
        { fromRow: 1, fromCol: 1, toRow: 1, toCol: 2 },
        { fromRow: 2, fromCol: 2, toRow: 2, toCol: 3 },
      ]
    }

    return scenarios[this.config.scenario] || null
  }

  /**
   * Get a pre-filled grid configuration for the current scenario
   * Returns a 2D array of cell type IDs, or null for random generation
   * 
   * Cell types: 1=RBC, 2=WBC, 3=Platelet, 4=Stem, 5=Cancer, 6=Donor
   */
  static getPrefilledGrid(): Array<Array<number>> | null {
    if (!this.isActive) return null

    const scenarios: Record<string, Array<Array<number>>> = {
      // Scenario 1: Early game - sparse board, few cells
      'early-game': [
        [0, 0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0, 0],
        [0, 0, 1, 2, 0, 0, 0],
        [0, 1, 2, 3, 1, 0, 0],
        [1, 2, 3, 1, 2, 3, 0]
      ],
      // Scenario 2: Mid game - moderate progress
      'mid-game': [
        [0, 0, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0, 0],
        [0, 0, 1, 2, 0, 0, 0],
        [0, 1, 2, 3, 1, 0, 0],
        [1, 2, 3, 1, 2, 3, 1],
        [2, 1, 2, 3, 1, 2, 3],
        [1, 3, 1, 2, 3, 1, 2]
      ],
      // Scenario 3: Big match - 5+ cells in a row
      'big-match': [
        [1, 2, 3, 1, 2, 3, 1],
        [2, 1, 2, 3, 1, 2, 3],
        [1, 1, 1, 1, 3, 2, 1],  // Row 2: Four 1s in a row
        [2, 3, 2, 3, 1, 3, 2],  // Swap (2,3) with (2,4) creates 5-match
        [3, 1, 2, 1, 2, 1, 3],
        [1, 2, 3, 2, 3, 2, 1],
        [2, 3, 1, 3, 1, 3, 2]
      ],
      // Scenario 4: Combo - cascade pattern
      'combo': [
        [1, 1, 2, 3, 2, 3, 1],
        [2, 2, 2, 1, 3, 1, 2],
        [3, 3, 1, 2, 1, 2, 3],
        [1, 1, 3, 3, 3, 1, 1],
        [2, 2, 1, 2, 1, 2, 2],
        [3, 1, 2, 1, 2, 3, 1],
        [1, 2, 3, 2, 3, 1, 2]
      ],
      // Scenario 5: Level up - multiple matches ready
      'level-up': [
        [1, 1, 2, 2, 3, 3, 1],
        [2, 2, 1, 1, 2, 2, 3],
        [3, 3, 3, 2, 1, 1, 2],
        [1, 1, 2, 3, 3, 2, 1],
        [2, 2, 1, 1, 2, 3, 3],
        [3, 3, 2, 2, 1, 1, 2],
        [1, 1, 3, 3, 2, 2, 1]
      ],
      // Scenario 6: High score - full board, active gameplay
      'high-score': [
        [1, 2, 3, 1, 2, 3, 1],
        [2, 3, 1, 2, 3, 1, 2],
        [3, 1, 2, 3, 1, 2, 3],
        [1, 2, 3, 1, 2, 3, 1],
        [2, 3, 1, 2, 3, 1, 2],
        [3, 1, 2, 3, 1, 2, 3],
        [1, 2, 3, 1, 2, 3, 1]
      ],
      // Scenario 7: Action shot - cells mid-fall
      'action-shot': [
        [0, 0, 0, 0, 0, 0, 0],
        [0, 1, 0, 2, 0, 3, 0],
        [1, 2, 3, 1, 2, 3, 1],
        [2, 3, 1, 2, 3, 1, 2],
        [3, 1, 2, 3, 1, 2, 3],
        [1, 2, 3, 1, 2, 3, 1],
        [2, 3, 1, 2, 3, 1, 2]
      ],
      // Legacy scenarios (keep for backwards compatibility)
      'bigmatch': [
        [1, 2, 3, 1, 2, 3, 1],
        [2, 1, 2, 3, 1, 2, 3],
        [1, 1, 1, 1, 3, 2, 1],
        [2, 3, 2, 3, 1, 3, 2],
        [3, 1, 2, 1, 2, 1, 3],
        [1, 2, 3, 2, 3, 2, 1],
        [2, 3, 1, 3, 1, 3, 2]
      ],
      'levelup': [
        [1, 1, 2, 2, 3, 3, 1],
        [2, 2, 1, 1, 2, 2, 3],
        [3, 3, 3, 2, 1, 1, 2],
        [1, 1, 2, 3, 3, 2, 1],
        [2, 2, 1, 1, 2, 3, 3],
        [3, 3, 2, 2, 1, 1, 2],
        [1, 1, 3, 3, 2, 2, 1]
      ],
      'achievement': [
        [1, 2, 3, 1, 2, 3, 1],
        [2, 3, 1, 2, 3, 1, 2],
        [3, 1, 2, 3, 1, 2, 3],
        [1, 2, 3, 1, 2, 3, 1],
        [2, 3, 1, 2, 3, 1, 2],
        [3, 1, 2, 3, 1, 2, 3],
        [1, 2, 3, 1, 2, 3, 1]
      ]
    }

    const grid = scenarios[this.config.scenario]
    if (grid) {
      Logger.getInstance().log('🎬 [ScreenshotMode] Using pre-filled grid for scenario:', this.config.scenario)
    }

    return grid || null
  }

  /**
   * Check if should use pre-filled grid
   */
  static shouldUsePrefilledGrid(): boolean {
    return this.isActive && this.getPrefilledGrid() !== null
  }
}

export interface ScreenshotConfig {
  disableAnimations: boolean
  deterministicGameplay: boolean
  skipIntro: boolean
  autoNavigate: boolean
  debugUI: boolean
  gameMode: 'endless' | 'timeattack'
  difficulty: 'Easy' | 'Normal' | 'Hard'
  scenario: string
  screenshotDelay: number
  // Scenario triggers
  triggerLevelUp: boolean
  triggerBigMatch: boolean
  triggerCombo: boolean
  triggerAchievement: boolean
}

// Add seedrandom type declaration
declare global {
  interface Math {
    seedrandom?: (seed: string) => () => number
  }
}